import {useState} from 'react';
import {commitTicketingMutation} from '../../../graphql/forecastRelay';
import CreateTimeRegMutation from '../../../../../mutations/ticketing/tickets/createTimeRegMutation';
import RecordTicketTimeRegistrationMutation from '../../../../../mutations/ticketing/tickets/recordTicketTimeRegistrationMutation';

export interface LogTimeToTaskInput {
	taskId: string;
	projectId: string | null;
	year: number;
	month: number;
	day: number;
	minutesRegistered: number;
	roleId?: string | null;
	notes?: string | null;
	personId: string;
	/**
	 * The ticket this time is being logged through. When set, the new registration
	 * is recorded against the ticket so it shows on the ticket's Time tab; when
	 * omitted the time is plain Forecast task time.
	 */
	ticketId?: string;
}

export interface LoggedTimeTask {
	id: string;
	totalMinutesRegistered: number | null;
}

export function useLogTimeToTask() {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	/** Second leg: map the new Forecast registration onto the ticket. */
	function recordProvenance(ticketId: string, timeRegId: string): Promise<void> {
		return new Promise((resolve, reject) => {
			commitTicketingMutation(
				RecordTicketTimeRegistrationMutation,
				{ticketId, timeRegId},
				() => resolve(),
				err => reject(err instanceof Error ? err : new Error(String(err)))
			);
		});
	}

	function submit(input: LogTimeToTaskInput): Promise<LoggedTimeTask> {
		setError(null);
		setLoading(true);
		const {ticketId, ...timeRegInput} = input;
		return new Promise((resolve, reject) => {
			const fail = (err: Error) => {
				setLoading(false);
				setError(err.message);
				reject(err);
			};

			commitTicketingMutation(
				CreateTimeRegMutation,
				timeRegInput,
				response => {
					const payload = response.createTimeReg;
					const errors: string[] | null = payload?.errors ?? null;
					if (errors && errors.length > 0) {
						fail(new Error(errors[0]));
						return;
					}
					const task = payload?.task;
					const loggedTask: LoggedTimeTask = {
						id: task?.id ?? timeRegInput.taskId,
						totalMinutesRegistered: task?.totalMinutesRegistered ?? null,
					};

					// Plain Forecast task time — nothing to attribute to a ticket.
					if (!ticketId) {
						setLoading(false);
						resolve(loggedTask);
						return;
					}

					const timeRegId = payload?.timeReg?.node?.id;
					if (!timeRegId) {
						// Without the new registration's id the mapping cannot be written,
						// so the ticket would silently never show this time.
						fail(new Error('Time was logged, but could not be attributed to this ticket. Please reload.'));
						return;
					}

					recordProvenance(ticketId, timeRegId).then(
						() => {
							setLoading(false);
							resolve(loggedTask);
						},
						// The registration exists in Forecast but is not linked to the
						// ticket. Surface it rather than resolving: the tab would come back
						// empty with no explanation.
						err =>
							fail(
								new Error(
									`Time was logged, but could not be attributed to this ticket: ${
										err instanceof Error ? err.message : String(err)
									}`
								)
							)
					);
				},
				err => fail(err instanceof Error ? err : new Error(String(err)))
			);
		});
	}

	return {submit, loading, error};
}
